/**
 * Content loader utility for handling H2-level chunked book content
 * This helps avoid loading large content into the context window
 */

import { Chapter, BookSection } from '../types/book';
import bookIndex from '../content/book-index-complete.json';

export class ContentLoader {
  private static sectionCache = new Map<string, string>();
  private static navigationMap = new Map<string, any>();

  // Initialize navigation map for quick lookups
  static {
    this.buildNavigationMap(bookIndex.navigation);
  }

  private static buildNavigationMap(navItems: any[]): void {
    for (const item of navItems) {
      this.navigationMap.set(item.id, item);
      if (item.children) {
        this.buildNavigationMap(item.children);
      }
    }
  }

  /**
   * Load section content by section ID
   * Each H2 section is now a self-contained file with all its H3 subsections
   */
  static async loadSectionContent(sectionId: string): Promise<string> {
    // Check cache first
    if (this.sectionCache.has(sectionId)) {
      return this.sectionCache.get(sectionId)!;
    }

    try {
      // Find the navigation item for this section
      const navItem = this.navigationMap.get(sectionId);
      if (!navItem || !navItem.filename) {
        throw new Error(`No filename found for section ${sectionId}`);
      }

      // Load the markdown file (H2 section with all H3 subsections included)
      const content = await this.loadSingleFile(navItem.filename);
      
      // Cache the result
      this.sectionCache.set(sectionId, content);
      
      return content;
    } catch (error) {
      console.error(`Error loading section content ${sectionId}:`, error);
      throw new Error(`Failed to load section content ${sectionId}`);
    }
  }

  /**
   * Load a single markdown file from the complete chapters directory
   */
  private static async loadSingleFile(filename: string): Promise<string> {
    try {
      // Use public directory path that works in both dev and production
      const response = await fetch(`/content/chapters-complete/${filename}`);
      if (!response.ok) {
        throw new Error(`Failed to load file ${filename}: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error(`Failed to load file ${filename}:`, error);
      throw new Error(`Failed to load file ${filename}`);
    }
  }

  /**
   * Load chapter content from individual markdown files
   */
  static async loadChapter(chapterId: string): Promise<Chapter> {
    try {
      // Find the chapter in navigation
      const navItem = this.navigationMap.get(chapterId);
      if (!navItem) {
        throw new Error(`Chapter ${chapterId} not found in navigation`);
      }

      const chapter: Chapter = {
        id: chapterId,
        title: navItem.title,
        sections: [],
        isExpanded: false
      };

      // Add sections from navigation
      if (navItem.children) {
        await this.addSectionsFromNavigation(navItem.children, chapter, chapterId);
      }

      return chapter;
    } catch (error) {
      console.error(`Error loading chapter ${chapterId}:`, error);
      throw new Error(`Failed to load chapter ${chapterId}`);
    }
  }

  private static async addSectionsFromNavigation(navItems: any[], chapter: Chapter, parentId: string): Promise<void> {
    for (const navItem of navItems) {
      const section: BookSection = {
        id: navItem.id,
        title: navItem.title,
        content: '', // Will be loaded on demand
        level: navItem.level,
        parentId: parentId
      };
      
      chapter.sections.push(section);
      
      // Recursively add child sections
      if (navItem.children && navItem.children.length > 0) {
        await this.addSectionsFromNavigation(navItem.children, chapter, parentId);
      }
    }
  }

  /**
   * Load raw markdown content for a chapter
   */
  static async loadChapterContent(chapterId: string): Promise<string> {
    try {
      // For chapter overview, load the chapter file
      const navItem = this.navigationMap.get(chapterId);
      if (!navItem || !navItem.filename) {
        throw new Error(`No filename found for chapter ${chapterId}`);
      }

      return await this.loadSingleFile(navItem.filename);
    } catch (error) {
      console.error(`Error loading chapter content ${chapterId}:`, error);
      throw new Error(`Failed to load chapter content ${chapterId}`);
    }
  }

  /**
   * Clear caches to free memory
   */
  static clearCache(): void {
    this.sectionCache.clear();
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): { sections: number } {
    return {
      sections: this.sectionCache.size
    };
  }
}
